import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Currency, Money } from '@principle-theorem/accounting';
import {
  isDepositAmount,
  type IBrand,
  type ITreatmentTemplate,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'pr-treatment-template',
  templateUrl: './treatment-template.component.html',
  styleUrls: ['./treatment-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TreatmentTemplateComponent {
  brand$ = new ReplaySubject<WithRef<IBrand>>(1);
  treatmentTemplate$ = new ReplaySubject<WithRef<ITreatmentTemplate>>(1);
  depositSummary$ = this._buildDepositSummary$();
  @Output() deleteTemplate = new EventEmitter<WithRef<ITreatmentTemplate>>();
  @Output() editTemplate = new EventEmitter<WithRef<ITreatmentTemplate>>();

  @Input()
  set brand(brand: WithRef<IBrand>) {
    if (brand) {
      this.brand$.next(brand);
    }
  }

  @Input()
  set treatmentTemplate(treatmentTemplate: WithRef<ITreatmentTemplate>) {
    if (treatmentTemplate) {
      this.treatmentTemplate$.next(treatmentTemplate);
    }
  }

  private _buildDepositSummary$(): Observable<string> {
    return combineLatest([this.brand$, this.treatmentTemplate$]).pipe(
      map(([brand, treatmentTemplate]) => {
        const settings = brand.settings.onlineBookings?.deposit;
        const override =
          settings?.treatmentTemplateOverrides?.[treatmentTemplate.ref.id];
        if (!settings?.enabled || override?.depositRequired !== true) {
          return 'No deposit required';
        }

        const amount = override.amount;
        return isDepositAmount(amount)
          ? `Deposit required: ${Money.formatPlainCents(
              amount.amountCents,
              Currency.AUD
            )}`
          : 'Deposit required: amount needs updating';
      })
    );
  }
}
